/**
 * NPIBadge — shows a warning banner when the selected SKU is an NPI
 * (New Product Introduction: fewer than 30 days of demand history).
 *
 * It fetches /api/npi/check/{sku_id} on mount / SKU change, and when
 * is_npi === true it surfaces the benchmark SKU, similarity score, and
 * an explanation so planners know the forecast is proxy-based.
 *
 * Usage:
 *   <NPIBadge skuId={params.sku_id} />
 */
import React, { useEffect, useState } from 'react';
import { apiGet } from '../../services_1/apiClient';
import './NPIBadge.css';

const NPIBadge = ({ skuId }) => {
  const [status, setStatus] = useState(null); // null = idle, 'loading', 'npi', 'mature', 'error'
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!skuId) {
      setStatus(null);
      setData(null);
      return;
    }

    let cancelled = false;
    setStatus('loading');

    apiGet(`/api/npi/check/${encodeURIComponent(skuId)}`)
      .then(d => {
        if (cancelled) return;
        setData(d);
        setStatus(d.is_npi ? 'npi' : 'mature');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => { cancelled = true; };
  }, [skuId]);

  if (!skuId || status === null || status === 'mature') return null;
  if (status === 'loading') {
    return (
      <div className="npi-badge npi-loading" aria-live="polite">
        <span className="npi-spinner" aria-hidden="true">⏳</span>
        <span>Checking NPI status…</span>
      </div>
    );
  }
  if (status === 'error') return null; // Fail silently — don't break the page

  // status === 'npi'
  const histDays   = data?.history_days ?? 0;
  const minDays    = data?.min_history_threshold ?? 30;
  const topBench   = data?.top_benchmarks?.[0];
  const benchSku   = topBench?.sku_id ?? '—';
  const similarity = topBench?.similarity_score != null
    ? `${(topBench.similarity_score * 100).toFixed(1)}%`
    : '—';

  return (
    <div className="npi-badge npi-warning" role="alert" aria-label="NPI Product Warning">
      <div className="npi-icon" aria-hidden="true">⚠️</div>
      <div className="npi-content">
        <strong className="npi-title">NPI Product — Proxy Forecast</strong>
        <p className="npi-desc">
          <strong>{skuId}</strong> has only <strong>{histDays} days</strong> of demand history
          (min&nbsp;{minDays} required). Forecast is based on benchmark SKU&nbsp;
          <strong className="npi-bench">{benchSku}</strong> (similarity&nbsp;{similarity}).
        </p>
        <p className="npi-hint">
          🔍 Treat this forecast as indicative. Accuracy will improve as more sales data is collected.
        </p>
      </div>
    </div>
  );
};

export default NPIBadge;
